import update from "immutability-helper";
import _ from "lodash";

import { isNonEmptyString, isNumber, isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { MEMBER_TYPES } from "globals/constants";

export const formatNumberAsPercent = (numberToFormat, defaultIfEmpty = "--") => {
	if (isNaN(Number(numberToFormat))) {
		return defaultIfEmpty;
	}

	return `${Number(numberToFormat).toFixed(2)}%`;
};

export const isPresent = value => value !== undefined && value !== null;

export const toNumber = number => {
	if (!isPresent(number)) {
		return 0;
	} else if (!isNaN(Number(number))) {
		return Number(number);
	} else if (number.replace) {
		return Number(number.replace(/\D/g, ""));
	} else if (!isNaN(parseFloat(number))) {
		return parseFloat(number);
	} else {
		return 0;
	}
};

export const withOnly1DecimalPlace = numberString => {
	if (hasMoreThan1Decimal(numberString)) {
		const separatedNumber = numberString.split(".");

		return `${separatedNumber[0]}.${separatedNumber[1].charAt(0)}`;
	}

	return numberString;
};

const hasMoreThan1Decimal = number => isNonEmptyString(number) && number.indexOf(".") > -1 && number.split(".")[1].length > 1;

const getPeriodDateString = (period, dateFormatter) =>
	period.startDate && period.endDate ?
		`: ${(dateFormatter(period.startDate))} - ${dateFormatter(period.endDate)}`
		: "";

const getPeriodOfString = (periodNumber, numberOfPeriods, periodOfTranslation) => {
	return periodOfTranslation
		.replace("{periodNumber}", periodNumber)
		.replace("{numberOfPeriods}", numberOfPeriods);
};

const generatePeriodLabel = (period, index, total, dateFormatter, periodOfTranslation) => {
	let periodOfString = getPeriodOfString(period.periodNumber, total, periodOfTranslation);
	let periodDateString = getPeriodDateString(period, dateFormatter);

	return `${periodOfString}${periodDateString}`;
};

export const generatePeriodDropdownItems = (periods, dateFormatter, periodOfTranslation) =>
	periods?.map((period, index) => ({
		value: period.periodNumber,
		key: index,
		text: generatePeriodLabel(period, index, periods.length, dateFormatter, periodOfTranslation)
	}));

export const moveItemInArrayTo = (array, indexToMove, indexToMoveTo) =>
	update(array, {
		$splice: [
			[ indexToMove, 1 ],
			[ indexToMoveTo, 0, array[indexToMove] ],
		],
	});

export const updateSortOfCategories = categories => {
	const newCategories = _.cloneDeep(categories);
	for (let i = 0; i < categories.length; i++) {
		let newCategory = newCategories[i];
		newCategory.sort = i;
		if (isNonEmptyArray(newCategory.items)) {
			for (let j = 0; j < newCategory.items.length; j++) {
				newCategory.items[j].sort = j;
			}
		}
	}

	return newCategories;
};

export const convertCategoriesToOptions = categories => categories.reduce((runningOptions, category) => {
	if (category.active && isNonEmptyArray(category.items)) {
		const newOptions = category.items
			.filter(item => item.active)
			.map(item => ({
				text: `${category.value} - ${item.value}`,
				value: item.hash,
			}));

		return [ ...runningOptions, ...newOptions ];
	}

	return runningOptions;
}, []);

export const convertOptionsToTextByValue = options => options.reduce((runningTextByValue, option) => ({
	...runningTextByValue,
	[option.value]: option.text,
}), []);

export const convertOptionsToOptionByValue = options => options.reduce((runningOptionsByValue, option) => ({
	...runningOptionsByValue,
	[option.value]: option,
}), []);

export const convertToLookupTable = (options, key, value = null) => options
	?.reduce((runningOptionsByValue, option) => ({
		...runningOptionsByValue,
		[option[key]]: isPresent(value) ? option[value] : option,
	}), []) || {};



export const getResearchTeamMemberDataByPeriodByMember = (researchTeamByPeriod, numberOfPeriods) => {
	let result = {};
	if (researchTeamByPeriod) {
		for (let periodNumber in researchTeamByPeriod) {
			const researchTeam = researchTeamByPeriod[periodNumber];
			if (isNonEmptyArray(researchTeam)) {
				for (let member of researchTeam) {
					let key = member.member + member.apptMonths;
					let memberData = result[key] || getDefaultMemberDataList(numberOfPeriods, member.name);
					memberData[periodNumber - 1] = member;
					result[key] = memberData;
				}
			}
		}
	}

	return result;
};

const getDefaultMemberDataList = (numberOfPeriods, name) => Array.from(Array(numberOfPeriods).keys())
	.map(() => ({ name }));

export const convertByPeriodToByItem = (byPeriod, numberOfPeriods, includeForEmptyPeriod = "name", key = "hash") => {
	let result = {};
	if (byPeriod) {
		for (let periodNumber in byPeriod) {
			const periodData = byPeriod[periodNumber];
			if (isNonEmptyArray(periodData)) {
				for (let item of periodData) {
					let resultKey = item[key];
					let memberData = result[resultKey] || getDefaultByItemList(
						numberOfPeriods,
						includeForEmptyPeriod,
						item[includeForEmptyPeriod]
					);
					memberData[periodNumber - 1] = item;
					result[resultKey] = memberData;
				}
			}
		}
	}

	return result;
};

const getDefaultByItemList = (numberOfPeriods, includeForEmptyPeriod = "name", value) => Array.from(Array(numberOfPeriods).keys())
	.map(() => ({ [includeForEmptyPeriod]: value }));

export const getPersonMonthsError = personMonths => {
	if (!personMonths) {
		return null;
	}

	const personMonthsNumber = toNumber(personMonths);
	if (personMonthsNumber !== 0 && (personMonthsNumber > 12.0 || personMonthsNumber < 0.1)) {
		return "numberOutOfRange";
	}

	return null;
};

export const getEffortError = effort => {
	if (!effort) {
		return null;
	}

	const effortNumber = toNumber(effort);
	if (effortNumber > 100 || effortNumber < 1) {
		return "numberOutOfRange";
	}

	return null;
};

export const willCopyToFromOverwriteData = (copyToFrom, formContent) => {
	if (isNonEmptyArray(copyToFrom)) {
		for (let copyToFromData of copyToFrom) {
			if (isNumber(copyToFromData?.from) && isNumber(copyToFromData?.to)) {
				const to = copyToFromData?.to;
				const hasExternalCostShareData = isNonEmptyArray(formContent?.externalCostShareByPeriod?.[to]);
				const hasInternalCostShareData = isNonEmptyArray(formContent?.internalCostShareByPeriod?.[to]);
				const hasSponsoredCostsData = isSponsoredCostDataModified(formContent?.sponsoredCostsByPeriod?.[to]);
				const hasResearchTeamData = isResearchTeamDataModified(formContent?.researchTeamByPeriod?.[to]);
				const hasPeriodData = isCurrentPeriodModified(formContent?.budgetPeriods?.periodRows, to);
				if (hasExternalCostShareData
					|| hasInternalCostShareData
					|| hasExternalCostShareData
					|| hasSponsoredCostsData
					|| hasResearchTeamData
					|| hasPeriodData
				) {
					return true;
				}
			}
		}
	}

	return false;
};

const isCurrentPeriodModified = (periodRows, copyToPeriodNumber) => {
	if (isNonEmptyArray(periodRows)) {
		for (let periodRow of periodRows) {
			if (periodRow.periodNumber === copyToPeriodNumber) {
				return periodRow?.costShare?.hasFringeRate
					|| periodRow?.costShare?.hasSalaryRate
				  || periodRow?.sponsorCosts?.hasFringeRate
					|| periodRow?.sponsorCosts?.hasSalaryRate;
			}
		}
	}

	return false;
};

const isSponsoredCostDataModified = sponsoredCostsForPeriod => {
	if (isNonEmptyArray(sponsoredCostsForPeriod)) {
		for (let sponsoredCost of sponsoredCostsForPeriod) {
			if (isNonEmptyArray(sponsoredCost.items)) {
				for (let item of sponsoredCost.items) {
					if (item.directCost !== 0 || item.isFA) {
						return true;
					}
				}
			}
		}
	}

	return false;
};

const isResearchTeamDataModified = researchTeamForPeriod => {
	if (isNonEmptyArray(researchTeamForPeriod)) {
		for (let member of researchTeamForPeriod) {
			if (member?.costShare
				|| member?.sponsoredCosts
				|| (member?.type === MEMBER_TYPES.MEMBER_UNNAMED && member?.baseSalary)
			) {
				return true;
			}
		}
	}

	return false;
};

/*
 * Begin budget calculation methods
 */

export const getResearchTeamTotal = researchTeamByPeriod => {
	let subtotal = 0;
	for (let periodNumber in researchTeamByPeriod) {
		subtotal += getPeriodSubtotal(researchTeamByPeriod[periodNumber], false);
	}

	return subtotal;
};

export const getPeriodSubtotal = (members, isCostShare) =>
	getRequestedFringeSubtotal(members, isCostShare) + getRequestedSalarySubtotal(members, isCostShare);

const getCostShareAmountAsNumber = category => {
	if (isPresent(category.costShareAmount)) {
		return toNumber(category.costShareAmount);
	}

	return 0;
};

export const getCostShareSubtotal = categories => {
	if (isNonEmptyArray(categories)) {
		return categories.reduce((runningTotal, category) => runningTotal + getCostShareAmountAsNumber(category), 0);
	}

	return 0;
};

const getFAAmount = (category, faRateAmount) => {
	if (isPresent(category?.costShareAmount) && category.isFA && !category.isResearchTeamMember) {
		return toNumber(category?.costShareAmount) * faRateAmount / 100;
	}

	return 0;
};

export const getFAAmountSubtotal = (categories, faRateAmount) => {
	let subtotal = 0;
	if (categories) {
		categories.forEach(category => {
			subtotal += getFAAmount(category, faRateAmount);
		});
	}

	return subtotal;
};

export const getPeriodTotalCost = (item, faRateAmount) => {
	if (isPresent(item.directCost)) {
		return toNumber(item.directCost) + getFACost(item, faRateAmount);
	}

	return 0;
};

export const getPeriodTotalCostSubtotal = (category, faRateAmount) => {
	let subtotal = 0;
	if (isPresent(category?.items)) {
		category.items.forEach(item => {
			subtotal += getPeriodTotalCost(item, parseFloat(faRateAmount));
		});
	}

	return subtotal;
};

export const getDirectCostSubtotal = category => {
	let subtotal = 0;
	if (category) {
		if (isPresent(category.items)) {
			category.items.forEach(item => {
				if (isPresent(item.directCost)) {
					subtotal += toNumber(item.directCost);
				}
			});
		}
	}

	return subtotal;
};

export const addDirectCosts = categories => {
	if (isNonEmptyArray(categories)) {
		return categories.reduce((runningTotal, category) => runningTotal + toNumber(category.directCost), 0);
	}

	return 0;
};

export const getLineItemsDirectCostTotal = categories => {
	let subtotal = 0;
	if (categories) {
		categories.forEach(category => {
			subtotal += getDirectCostSubtotal(category);
		});
	}

	return subtotal;
};

export const getFACost = (item, faRateAmount) => {
	if (item.isFA && item.directCost) {
		if (!faRateAmount && item?.FA) {
			return toNumber(item.directCost) * toNumber(item.FA) / 100;
		}

		return toNumber(item.directCost) * faRateAmount / 100;
	}

	return 0;
};

export const getFACostSubtotal = (category, faRateAmount) => {
	let subtotal = 0;
	if (isPresent(category?.items)) {
		category.items.forEach(item => {
			subtotal += getFACost(item, faRateAmount);
		});
	}

	return subtotal;
};

export const getResearchTeamFACostsByPeriod = (researchTeamByPeriod, periodList) => {
	let result = {};
	for (let period of periodList) {
		result[period.periodNumber] = getRateSubtotal(
			period.rateForPeriod,
			researchTeamByPeriod?.[period?.periodNumber],
			period?.sponsorCosts?.hasFringeRate,
			period?.sponsorCosts?.hasSalaryRate,
			false
		);
	}

	return result;
};

export const getTotalFACostsByPeriod = (sponsoredCostsByPeriod, subawardCap, researchTeamByPeriod, periodList) => {
	const faCostsByPeriod = getFACostsByPeriod(sponsoredCostsByPeriod, subawardCap, periodList);
	let result = {};
	for (let period of periodList) {
		let subtotal = 0;
		subtotal += faCostsByPeriod?.[period?.periodNumber];
		subtotal += getRateSubtotal(
			period.rateForPeriod,
			researchTeamByPeriod?.[period?.periodNumber],
			period?.sponsorCosts?.hasFringeRate,
			period?.sponsorCosts?.hasSalaryRate,
			false
		);
		result[period.periodNumber] = subtotal;
	}

	return result;
};

export const getFACostsByPeriod = (sponsoredCostsByPeriod, subawardCap, periodList) => {
	const ratesByPeriod = convertToLookupTable(periodList, "periodNumber", "rateForPeriod");
	let result = {};
	let subawardCaps = {};
	for (let period of periodList) {
		const periodNumber = period.periodNumber;
		let faCostForPeriod = 0;
		const sponsoredCosts = sponsoredCostsByPeriod[periodNumber];
		const subawardCost = sponsoredCosts?.find(cost => cost.hash === "subawards");
		if (subawardCost && isNonEmptyArray(subawardCost.items)) {
			for (let item of subawardCost.items) {
				const remainingCap = subawardCaps[item.value] || subawardCap;

				const categoriesByPeriod = getSubawardCategoriesByValue(sponsoredCostsByPeriod);
				const currentCapSpace = getSubawardCapSpaceForPeriod(categoriesByPeriod?.[item.value], subawardCap, periodNumber);
				const cost = toNumber(item.directCost) > currentCapSpace ? currentCapSpace : toNumber(item.directCost);

				const faRate = item?.FA || ratesByPeriod[periodNumber];
				const faCost = toNumber(cost) * toNumber(faRate) / 100;
				faCostForPeriod += faCost;
				subawardCaps[item.value] = remainingCap - cost;
			}
		}
		const nonSubawardSponsoredCosts = sponsoredCosts?.filter(cost => cost.hash !== "subawards");
		faCostForPeriod += getLineItemsFACostTotal(nonSubawardSponsoredCosts, ratesByPeriod[periodNumber]);
		result[periodNumber] = faCostForPeriod;
	}

	return result;
};

export const getNonSubawardLineItemsFACostTotal = (categories, faRateAmount) => {
	if (isNonEmptyArray(categories)) {
		const categoriesToUse = categories?.filter(category => category.value !== "Subawards");

		return getLineItemsFACostTotal(categoriesToUse, faRateAmount);
	}

	return 0;
};

export const getLineItemsFACostTotal = (categories, faRateAmount) => {
	let subtotal = 0;
	if (categories) {
		categories.forEach(category => {
			subtotal += getFACostSubtotal(category, faRateAmount);
		});
	}

	return subtotal;
};

export const getSubawardCapSpaceForPeriod = (categoriesByPeriod, subawardCostCap, currentPeriodNumber) => {
	let cap = toNumber(subawardCostCap);
	for (let i = 1; i < currentPeriodNumber; i++) {
		const directCost = toNumber(categoriesByPeriod?.[i]?.directCost);
		if (!isNaN(directCost)) {
			cap = Math.max(cap - directCost, 0);
		}
	}

	return cap;
};

export const getSubawardCategoriesByValue = sponsoredCostsByPeriod => {
	const categoriesByValue = {};
	for (const periodNumber in sponsoredCostsByPeriod) {
		const sponsoredCosts = sponsoredCostsByPeriod?.[periodNumber];
		sponsoredCosts && sponsoredCosts
			.filter(({ value, items }) => value === "Subawards" && isNonEmptyArray(items))
			.flatMap(({ items }) => items)
			.forEach(item => {
				const categoriesByPeriod = categoriesByValue[item.value] || {};
				categoriesByPeriod[periodNumber] = item;
				categoriesByValue[item.value] = categoriesByPeriod;
			});
	}

	return categoriesByValue;
};

const getSubawardItemFACost = (item, currentCapSpace) => {
	if (item.isFA && item.directCost) {
		const directCost = toNumber(item.directCost);
		const rate = toNumber(item.FA);
		const cost = directCost > currentCapSpace ? currentCapSpace : directCost;

		return cost * rate / 100;
	}

	return 0;
};

export const getSubawardsFACostTotalForPeriod = (sponsoredCostsByPeriod, subawardCostCap, currentPeriodNumber) => {
	const subawardsFACostByValue = getSubawardsFACostForPeriodByValue(
		sponsoredCostsByPeriod,
		subawardCostCap,
		currentPeriodNumber
	);

	return Object.values(subawardsFACostByValue).reduce((subawardTotal, valueTotal) => subawardTotal + valueTotal, 0);
};

export const getSubawardsFACostForPeriodByValue = (sponsoredCostsByPeriod, subawardCostCap, currentPeriodNumber) => {
	const categoriesByValue = getSubawardCategoriesByValue(sponsoredCostsByPeriod);
	const faCostByValue = {};
	for (const value in categoriesByValue) {
		let categoriesByPeriod = categoriesByValue?.[value];
		const capSpace = getSubawardCapSpaceForPeriod(categoriesByPeriod, subawardCostCap, currentPeriodNumber);
		const item = categoriesByPeriod?.[currentPeriodNumber];
		if (item) {
			faCostByValue[value] = getSubawardItemFACost(item, capSpace);
		}
	}

	return faCostByValue;
};

export const getRateSubtotal = (faRateAmount, categories, hasFringeRate, hasSalaryRate, isCostShare) => {
	let rateSubtotal = 0;

	if (hasFringeRate) {
		rateSubtotal += getRequestedFringeSubtotal(categories, isCostShare) * (faRateAmount / 100);
	}
	if (hasSalaryRate) {
		rateSubtotal += getRequestedSalarySubtotal(categories, isCostShare) * (faRateAmount / 100);
	}

	return rateSubtotal;
};

export const getRequestedSalarySubtotal = (members, isCostShare) => {
	let subtotal = 0;
	if (isNonEmptyArray(members)) {
		return members.reduce(
			(runningSubtotal, member) => runningSubtotal + getRequestedSalary(member, isCostShare),
			0
		);
	}

	return subtotal;
};

export const getRequestedFringeSubtotal = (members, isCostShare) => {
	if (isNonEmptyArray(members)) {
		return members.reduce(
			(runningSubtotal, member) => runningSubtotal + getFringeRequested(member, isCostShare),
			0
		);
	}

	return 0;
};

export const getMemberSubtotal = (member, isCostShare) =>
	getFringeRequested(member, isCostShare) + getRequestedSalary(member, isCostShare);

export const getFringeRequested = (member, isCostShare) => {
	const memberData = isCostShare ? member?.costShare : member?.sponsoredCosts;
	if (isPresent(memberData?.fringeRate)) {
		memberData.fringeRequested = getRequestedSalary(member, isCostShare) * (memberData.fringeRate / 100);

		return memberData.fringeRequested;
	}

	return 0;
};

export const getRequestedSalary = (member, isCostShare) => {
	const memberData = isCostShare ? member?.costShare : member?.sponsoredCosts;
	if (isPresent(memberData?.baseSalary) && isPresent(memberData?.effort)) {
		memberData.reqSalary = toNumber(memberData.baseSalary) * (toNumber(memberData.effort) / 100);

		return memberData.reqSalary;
	}

	return 0;
};

/*
 * End budget calculation methods
 */

